import React, { useEffect, useMemo, useState } from "react";
import { Bell, CheckCheck, Circle, FileText, FolderClosed, ListChecks, ShieldCheck, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TESTIDS } from "@/constants/testIds";

const iconFor = (type) => {
  if (type.startsWith("review")) return ShieldCheck;
  if (type.startsWith("file")) return FolderClosed;
  if (type.startsWith("task")) return ListChecks;
  if (type.startsWith("document")) return FileText;
  return Bell;
};

const tone = {
  critical: "text-red-300 border-red-500/30",
  warning: "text-amber-300 border-amber-500/30",
  success: "text-emerald-300 border-emerald-500/30",
  info: "text-sky-300 border-sky-500/30",
};

function errorDetail(error, fallback) {
  const value = error?.response?.data?.detail;
  return typeof value === "string" ? value : fallback;
}

const refreshBadge = () => window.dispatchEvent(new Event("td-notifications-changed"));

export default function NotificationsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("unread");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setItems(await api.listNotifications()); }
    catch (error) { toast.error(errorDetail(error, t("notifications_load_failed"))); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(
    () => items.filter((item) => filter === "all" || !item.read_at),
    [items, filter],
  );
  const unread = items.filter((item) => !item.read_at).length;

  const markRead = async (item, open = false) => {
    try {
      const updated = item.read_at ? item : await api.markNotificationRead(item.id);
      setItems((values) => values.map((value) => value.id === item.id ? updated : value));
      refreshBadge();
      if (open && item.action_url) navigate(item.action_url);
    } catch (error) {
      toast.error(errorDetail(error, t("notifications_update_failed")));
    }
  };

  const readAll = async () => {
    try {
      await api.markAllNotificationsRead();
      const stamp = new Date().toISOString();
      setItems((values) => values.map((item) => item.read_at ? item : { ...item, read_at: stamp }));
      refreshBadge();
    } catch (error) { toast.error(errorDetail(error, t("notifications_update_failed"))); }
  };

  const remove = async (item) => {
    try {
      await api.deleteNotification(item.id);
      setItems((values) => values.filter((value) => value.id !== item.id));
      refreshBadge();
    } catch (error) { toast.error(errorDetail(error, t("notifications_delete_failed"))); }
  };

  return (
    <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12 max-w-5xl" data-testid={TESTIDS.notificationsPage}>
      <div className="flex items-start justify-between gap-5 flex-wrap mb-10">
        <div>
          <div className="kicker mb-3">— {t("notifications_title")}</div>
          <h1 className="font-serif text-4xl md:text-5xl mb-3">{t("notifications_title")}</h1>
          <p className="text-[var(--td-text-2)] max-w-2xl leading-relaxed">{t("notifications_intro")}</p>
        </div>
        <button type="button" onClick={readAll} disabled={!unread} data-testid={TESTIDS.notificationsReadAll}
          className="btn-ghost inline-flex items-center gap-2 disabled:opacity-40">
          <CheckCheck size={14} /> {t("notifications_read_all")}
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        {["unread", "all"].map((value) => (
          <button key={value} type="button" onClick={() => setFilter(value)}
            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${filter === value ? "border-[var(--td-gold)] text-[var(--td-gold)]" : "border-[var(--td-border)] text-[var(--td-text-3)]"}`}>
            {t(`notifications_filter_${value}`)} {value === "unread" ? `(${unread})` : ""}
          </button>
        ))}
      </div>

      <section className="card-surface overflow-hidden">
        {loading ? <div className="p-8 text-[var(--td-text-3)]">…</div> : visible.length === 0 ? (
          <div className="p-14 text-center">
            <Bell size={30} className="mx-auto mb-3 text-[var(--td-gold)]" />
            <h2 className="font-serif text-xl">{filter === "unread" ? t("notifications_empty_unread") : t("notifications_empty_all")}</h2>
          </div>
        ) : visible.map((item) => {
          const Icon = iconFor(item.type);
          return (
            <article key={item.id} data-testid={TESTIDS.notificationRow}
              className={`flex items-start gap-4 p-5 border-b border-[var(--td-border)] last:border-b-0 ${item.read_at ? "opacity-60" : "bg-white/[0.015]"}`}>
              <div className={`w-9 h-9 border flex items-center justify-center shrink-0 ${tone[item.severity] || tone.info}`}><Icon size={16} /></div>
              <button type="button" onClick={() => markRead(item, true)} className="text-left min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  {!item.read_at && <Circle size={7} fill="currentColor" className="text-[var(--td-gold)]" />}
                  <h3 className="font-serif text-lg">{item.title}</h3>
                  <span className={`font-mono text-[9px] uppercase tracking-widest ${tone[item.severity]?.split(" ")[0] || ""}`}>{item.severity}</span>
                </div>
                <p className="text-sm text-[var(--td-text-2)] leading-relaxed">{item.message}</p>
                <div className="text-[10px] font-mono text-[var(--td-text-3)] mt-2">{new Date(item.created_at).toLocaleString()}</div>
              </button>
              {!item.read_at && <button type="button" onClick={() => markRead(item)} title={t("notifications_mark_read")} className="text-[var(--td-text-3)] hover:text-emerald-300"><CheckCheck size={15} /></button>}
              <button type="button" onClick={() => remove(item)} title={t("notifications_delete")} data-testid={TESTIDS.notificationDelete} className="text-[var(--td-text-3)] hover:text-red-400"><Trash2 size={14} /></button>
            </article>
          );
        })}
      </section>
    </div>
  );
}
